import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { morseToText, textToMorse } from "../lib/morse";

type Mode = "encode" | "decode";
const SAMPLE = "SOS";

export function MorseTool() {
  const uid = useId();
  const [mode, setMode] = useState<Mode>("encode");
  const [input, setInput] = useState(SAMPLE);
  const [copied, setCopied] = useState(false);

  const output = useMemo(
    () => (mode === "encode" ? textToMorse(input) : morseToText(input)),
    [mode, input],
  );

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-lg border border-border bg-card/60 p-1">
        <button
          type="button"
          aria-pressed={mode === "encode"}
          onClick={() => setMode("encode")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "encode"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.mc_mode_encode()}
        </button>
        <button
          type="button"
          aria-pressed={mode === "decode"}
          onClick={() => setMode("decode")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "decode"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.mc_mode_decode()}
        </button>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${uid}-in`}>{m.mc_input_label()}</Label>
        <textarea
          id={`${uid}-in`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            mode === "encode"
              ? m.mc_placeholder_encode()
              : m.mc_placeholder_decode()
          }
          spellCheck={false}
          className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-out`}>{m.mc_output_label()}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={copy}
            disabled={!output}
            className="gap-2"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? m.mc_copied() : m.mc_copy()}
          </Button>
        </div>
        <textarea
          id={`${uid}-out`}
          readOnly
          data-testid="mc-output"
          value={output}
          spellCheck={false}
          className="min-h-[8rem] w-full resize-y break-all rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
      </div>
    </div>
  );
}
